import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { EmailTemplates as EmailTemplatesApi } from "@apis/index";
import Editor from "./editor";
import { toast } from "react-toastify";

const categories = [
  { id: "admin", label: "Admin Mail Templates" },
  { id: "store", label: "Store Mail Templates" },
  { id: "deliveryman", label: "Delivery Man Mail Templates" },
  { id: "customer", label: "Customer Mail Templates" },
];

const eventsConfig: any = {
  admin: [
    { id: "forgot_password", label: "Forgot Password" },
    { id: "new_store_registration", label: "New Store Registration" },
    { id: "new_deliveryman_registration", label: "New Delivery Man Registration" },
    { id: "withdraw_request", label: "Withdraw Request" },
    { id: "deliveryman_withdraw_request", label: "Delivery Man Withdraw Request" },
  ],
  store: [
    { id: "registration", label: "Store Registration" },
    { id: "approval", label: "Store Approval" },
    { id: "rejection", label: "Store Rejection" },
    { id: "suspension", label: "Account Suspension" },
    { id: "withdrawal_approved", label: "Withdrawal Approved" },
    { id: "withdrawal_rejected", label: "Withdrawal Rejected" },
    { id: "forgot_password", label: "Forgot Password" },
  ],
  deliveryman: [
    { id: "registration", label: "Registration" },
    { id: "approval", label: "Approval" },
    { id: "rejection", label: "Rejection" },
    { id: "suspension", label: "Account Suspension" },
    { id: "withdrawal_approved", label: "Withdrawal Approved" },
    { id: "withdrawal_rejected", label: "Withdrawal Rejected" },
    { id: "forgot_password", label: "Forgot Password" },
  ],
  customer: [
    { id: "registration", label: "New Customer Registration" },
    { id: "otp_verification", label: "OTP Verification" },
    { id: "forgot_password", label: "Forgot Password" },
    { id: "order_confirmation", label: "Order Placement Confirmation" },
    { id: "suspension", label: "Account Suspension" },
    { id: "cash_collection", label: "Cash Collection Notification" },
  ],
};

const EmailTemplates = () => {
  const [activeCategory, setActiveCategory] = useState("admin");
  const [activeEvent, setActiveEvent] = useState("");
  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventsConfig[activeCategory]?.length > 0) {
      setActiveEvent(eventsConfig[activeCategory][0].id);
    }
  }, [activeCategory]);

  useEffect(() => {
    if (activeCategory && activeEvent) {
      fetchTemplate();
    }
  }, [activeCategory, activeEvent]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const data = await EmailTemplatesApi.getOne({ type: activeCategory, event: activeEvent } as any);
      setTemplate(data || {
        type: activeCategory,
        event: activeEvent,
        subject: "",
        body: "",
        isActive: true,
        variables: [],
      });
    } catch (error) {
      console.error("Failed to fetch template:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: any) => {
    try {
      await EmailTemplatesApi.upsert({ data });
      toast.success("Template saved successfully");
      fetchTemplate();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  return (
    <Container>
      <Header>
        <Title>Email Templates</Title>
        <CategorySelect 
          value={activeCategory} 
          onChange={(e) => setActiveCategory(e.target.value)}
        >
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.label}
            </option>
          ))}
        </CategorySelect>
      </Header>

      <EventTabs>
        {eventsConfig[activeCategory]?.map((event: any) => (
          <Tab
            key={event.id}
            active={activeEvent === event.id}
            onClick={() => setActiveEvent(event.id)}
          >
            {event.label}
          </Tab>
        ))}
      </EventTabs>

      {loading ? (
        <Loading>Loading template...</Loading>
      ) : (
        <Editor 
          template={template} 
          onSave={handleSave} 
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
`;

const CategorySelect = styled.select`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  &:focus {
    border-color: #3182ce;
  }
`;

const EventTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: 20px;
  background: ${(props) => (props.active ? "#4a5568" : "#f7fafc")};
  color: ${(props) => (props.active ? "white" : "#4a5568")};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${(props) => (props.active ? "#4a5568" : "#e2e8f0")};
  &:hover {
    background: ${(props) => (props.active ? "#2d3748" : "#edf2f7")};
  }
`;

const Loading = styled.div`
  padding: 40px;
  text-align: center;
  color: #718096;
`;

export default EmailTemplates;
